import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPostById, getVisiblePosts } from '../data/blogPosts';
import { getAuthorRole } from '../data/team';

// Robot/AI icon component for anonymous posts
const RobotIcon: React.FC = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        {/* Robot head/body */}
        <rect x="6" y="8" width="12" height="10" rx="2" ry="2" fill="#3F013D" opacity="0.9" />
        <rect x="6" y="8" width="12" height="10" rx="2" ry="2" fill="#c084fc" opacity="0.9" />




        {/* Antenna */}
        <line x1="12" y1="6" x2="12" y2="4" stroke="#ae4094" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="3" r="1" fill="#ff6b9d" />

        {/* Eyes */}
        <circle cx="9.5" cy="11" r="1.5" fill="white" />
        <circle cx="14.5" cy="11" r="1.5" fill="white" />
        <circle cx="9.5" cy="11" r="0.7" fill="#c084fc" />
        <circle cx="14.5" cy="11" r="0.7" fill="#c084fc" />
        <circle cx="9.5" cy="11" r="0.3" fill="#111" />
        <circle cx="14.5" cy="11" r="0.3" fill="#111" />

        {/* Happy mouth */}
        <path d="M 10 14 Q 12 16 14 14" stroke="#ae4094" strokeWidth="1.5" strokeLinecap="round" fill="none" />

        {/* Circuit pattern decorations */}
        <line x1="7" y1="19" x2="9" y2="19" stroke="#ff6b9d" strokeWidth="1" opacity="0.8" />
        <line x1="15" y1="19" x2="17" y2="19" stroke="#ff6b9d" strokeWidth="1" opacity="0.8" />
        <circle cx="8" cy="19" r="0.5" fill="#ae4094" opacity="0.7" />
        <circle cx="16" cy="19" r="0.5" fill="#ae4094" opacity="0.7" />

        {/* Side ports/connectors */}
        <rect x="5.5" y="12" width="1" height="2" rx="0.5" fill="#3F013D" opacity="0.8" />
        <rect x="17.5" y="12" width="1" height="2" rx="0.5" fill="#3F013D" opacity="0.8" />
    </svg>
);

// Content renderer component for safe HTML rendering
const ContentRenderer: React.FC<{ content: string }> = ({ content }) => {
    // Parse the HTML content and render it safely
    const parseContent = (htmlContent: string) => {
        // Split content by HTML tags and process each part
        const parts = htmlContent.split(/(<[^>]+>)/);
        const elements: React.ReactNode[] = [];
        let key = 0;

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];

            if (part.startsWith('<') && part.endsWith('>')) {
                // This is an HTML tag
                const tag = part.slice(1, -1);
                const tagName = tag.split(' ')[0];

                if (tagName.toLowerCase() === 'img') {
                    const srcMatch = tag.match(/src="([^"]*)"/);
                    const altMatch = tag.match(/alt="([^"]*)"/);
                    const src = srcMatch ? srcMatch[1] : '';
                    const alt = altMatch ? altMatch[1] : '';
                    elements.push(
                        <img key={key++} src={src} alt={alt} className="rounded-lg my-8 w-full shadow-lg" />
                    );
                    continue;
                }

                if (tagName.startsWith('/')) {
                    // Closing tag - skip for now as we handle opening/closing together
                    continue;
                }

                // Find the closing tag
                const closingTag = `</${tagName}>`;
                let content = '';
                let j = i + 1;
                let depth = 1;

                while (j < parts.length && depth > 0) {
                    if (parts[j] === `<${tagName}>`) {
                        depth++;
                    } else if (parts[j] === closingTag) {
                        depth--;
                    }

                    if (depth > 0) {
                        content += parts[j];
                    }
                    j++;
                }

                // Skip processed parts
                i = j - 1;

                // Render the appropriate component
                switch (tagName.toLowerCase()) {
                    case 'h2':
                        elements.push(
                            <h2 key={key++} className="text-2xl md:text-3xl font-bold mt-12 mb-6 text-white">
                                {content}
                            </h2>
                        );
                        break;
                    case 'h3':
                        elements.push(
                            <h3 key={key++} className="text-xl md:text-2xl font-bold mt-8 mb-4 text-white">
                                {content}
                            </h3>
                        );
                        break;
                    case 'p':
                        elements.push(
                            <p key={key++} className="text-gray-300 leading-relaxed mb-6 text-lg">
                                {parseInlineContent(content)}
                            </p>
                        );
                        break;
                    case 'ul':
                        const listItems = content.split(/<\/?li>/g).filter(item => item.trim());
                        elements.push(
                            <ul key={key++} className="list-disc pl-6 mb-6 space-y-2">
                                {listItems.map((item, idx) => (
                                    <li key={idx} className="text-gray-300 leading-relaxed">
                                        {parseInlineContent(item)}
                                    </li>
                                ))}
                            </ul>
                        );
                        break;
                    case 'ol':
                        const orderedItems = content.split(/<\/?li>/g).filter(item => item.trim());
                        elements.push(
                            <ol key={key++} className="list-decimal pl-6 mb-6 space-y-2">
                                {orderedItems.map((item, idx) => (
                                    <li key={idx} className="text-gray-300 leading-relaxed">
                                        {parseInlineContent(item)}
                                    </li>
                                ))}
                            </ol>
                        );
                        break;
                    default:
                        // For unknown tags, just render the content
                        elements.push(
                            <div key={key++} className="mb-4">
                                {parseInlineContent(content)}
                            </div>
                        );
                }
            } else if (part.trim()) {
                // This is text content
                elements.push(
                    <p key={key++} className="text-gray-300 leading-relaxed mb-6 text-lg">
                        {parseInlineContent(part)}
                    </p>
                );
            }
        }

        return elements;
    };

    // Parse inline content (bold, italic, etc.)
    const parseInlineContent = (text: string): React.ReactNode => {
        if (!text) return '';

        // Handle strong, em, and anchor tags
        const parts = text.split(/(<strong>.*?<\/strong>|<em>.*?<\/em>|<a.*?<\/a>)/g).filter(Boolean);

        return parts.map((part, index) => {
            if (part.startsWith('<strong>')) {
                const content = part.replace(/<\/?strong>/g, '');
                return <strong key={index} className="font-bold text-white">{content}</strong>;
            }
            if (part.startsWith('<em>')) {
                const content = part.replace(/<\/?em>/g, '');
                return <em key={index} className="italic text-gray-300">{content}</em>;
            }
            if (part.startsWith('<a')) {
                const hrefMatch = part.match(/href="([^"]*)"/);
                const targetMatch = part.match(/target="([^"]*)"/);
                const content = part.replace(/<a.*?>|<\/a>/g, '');
                const href = hrefMatch ? hrefMatch[1] : '#';

                return (
                    <a
                        key={index}
                        href={href}
                        target={targetMatch ? targetMatch[1] : '_blank'}
                        rel="noopener noreferrer"
                        className="text-accent-light hover:underline"
                    >
                        {content}
                    </a>
                );
            }
            return part;
        });
    };

    return (
        <div className="prose prose-lg max-w-none">
            {parseContent(content)}
        </div>
    );
};

// Share icon component
const ShareIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L12 15M12 2L8 6M12 2L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 9H5C3.89543 9 3 9.89543 3 11V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V11C21 9.89543 20.1046 9 19 9H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Helper functions for related postss
const getRelatedPostsByCategory = (currentPostId: number, category: string) => {
    const allPosts = getVisiblePosts();
    return allPosts
        .filter(post => post.category === category && post.id !== currentPostId)
        .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            const dateComparison = dateB.getTime() - dateA.getTime();
            if (dateComparison === 0) {
                return b.id - a.id;
            }
            return dateComparison;
        })
        .slice(0, 16); // Limit to 16 posts
};

const getRelatedPostsByAuthor = (currentPostId: number, author: string) => {
    const allPosts = getVisiblePosts();
    return allPosts
        .filter(post => post.author === author && post.id !== currentPostId)
        .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            const dateComparison = dateB.getTime() - dateA.getTime();
            if (dateComparison === 0) {
                return b.id - a.id;
            }
            return dateComparison;
        })
        .slice(0, 16); // Limit to 16 posts
};

const getAllCategories = () => {
    const allPosts = getVisiblePosts();
    return Array.from(new Set(allPosts.map(post => post.category))).sort();
};

// Related Posts Section Component
const RelatedPostsSection: React.FC<{ title: string; posts: any[] }> = ({ title, posts }) => {
    if (posts.length === 0) return null;

    return (
        <div>
            <h3 className="text-2xl font-bold mb-6 text-white">{title}</h3>
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                {posts.map(post => (
                    <Link
                        key={post.id}
                        to={`/augury/${post.id}`}
                        className="flex-shrink-0 w-80 bg-gray-900 bg-opacity-50 rounded-lg overflow-hidden transform transition-all hover:scale-102 hover:bg-opacity-70 group"
                    >
                        <div className="p-6">
                            <h4 className="text-lg font-bold mb-3 leading-tight group-hover:text-accent-light transition-colors">
                                {post.title}
                            </h4>
                            <p className="text-gray-300 mb-4 text-sm line-clamp-3">
                                {post.excerpt}
                            </p>
                            <div className="flex items-center justify-between text-sm text-gray-400">
                                <span>{post.date}</span>
                                <span>{post.readTime}</span>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

const AuguryPostPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [showShareTooltip, setShowShareTooltip] = React.useState(false);
    const [shareButtonText, setShareButtonText] = React.useState('Share article link');
    const [isEmailBannerMinimized, setIsEmailBannerMinimized] = React.useState(false);

    const handleShareClick = async () => {
        try {
            const currentUrl = window.location.href;

            // Check if clipboard API is available
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(currentUrl);
                setShareButtonText('Link copied!');
                setTimeout(() => {
                    setShareButtonText('Share article link');
                }, 2000);
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = currentUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                    document.execCommand('copy');
                    setShareButtonText('Link copied!');
                    setTimeout(() => {
                        setShareButtonText('Share article link');
                    }, 2000);
                } catch (fallbackErr) {
                    console.error('Fallback copy failed:', fallbackErr);
                    setShareButtonText('Copy failed');
                    setTimeout(() => {
                        setShareButtonText('Share article link');
                    }, 2000);
                } finally {
                    document.body.removeChild(textArea);
                }
            }
        } catch (err) {
            console.error('Failed to copy link:', err);
            setShareButtonText('Copy failed');
            setTimeout(() => {
                setShareButtonText('Share article link');
            }, 2000);
        }
    };

    const post = getPostById(parseInt(id || '0'));

    if (!post) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 md:py-24">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4 text-white">Post Not Found</h1>
                    <p className="text-gray-300 mb-8">The blog post you're looking for doesn't exist or is not currently available.</p>
                    <Link to="/augury" className="text-accent-light hover:text-accent transition-colors">
                        ← Back to Augury
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-24">
            {/* Back Navigation */}
            <div className="mb-8">
                <Link
                    to="/augury"
                    className="text-accent-light hover:text-accent transition-colors flex items-center group"
                >
                    <span className="transform group-hover:-translate-x-1 transition-transform">←</span>
                    <span className="ml-2">Back to Augury</span>
                </Link>
            </div>

            {/* Article Header */}
            <article className="max-w-none">
                <div className="mb-12">
                    <div className="flex items-center text-gray-400 text-sm mb-6">
                        <span>{post.date}</span>
                        <span className="mx-2 text-gray-600">•</span>
                        <span>{post.readTime}</span>
                        <span className="mx-2 text-gray-600">•</span>
                        <Link to={`/augury?category=${encodeURIComponent(post.category)}`} className="text-accent-light font-medium hover:underline transition-colors">
                            {post.category}
                        </Link>
                    </div>

                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tighter">
                        <span className="bg-gradient-to-r from-accent to-accent-light bg-clip-text text-transparent" style={{
                            textShadow: 'rgb(255 255 255) 1px 1px 0px, rgb(59 130 246 / 0.5) -1px -1px 0px, rgb(255 255 255) 0px 1px 0px, rgb(255 255 255) 1px 0px 0px, #ae4094 0px -1px 0px, #ae4094 -1px 0px 0px'
                        }}>
                            {post.title}
                        </span>
                    </h1>

                    <div className="flex items-center justify-between mb-12 p-6 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center mr-6 shadow-lg">
                                {post.author === "Anonymously Generated" ? (
                                    <div className="text-white">
                                        <RobotIcon />
                                    </div>
                                ) : (
                                        <span className="font-bold text-xl text-white">
                                            {post.author.split(' ').map(n => n[0]).join('')}
                                        </span>
                                )}
                            </div>
                            <div>
                                <div className="font-semibold text-lg text-white">{post.author}</div>
                                <div className="text-gray-400">{getAuthorRole(post.author)}</div>
                            </div>
                        </div>

                        {/* Share Button */}
                        <div className="relative">
                            <button
                                onClick={handleShareClick}
                                onMouseEnter={() => setShowShareTooltip(true)}
                                onMouseLeave={() => setShowShareTooltip(false)}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors text-gray-300 hover:text-white"
                                aria-label="Share article"
                            >
                                <ShareIcon />
                            </button>

                            {/* Tooltip */}
                            {showShareTooltip && (
                                <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap shadow-lg border border-gray-700">
                                    {shareButtonText}
                                    <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Article Content */}
                <div className="article-content">
                    <ContentRenderer content={post.content} />
                </div>
            </article>

            {/* Related Posts Sections */}
            <div className="mt-16 space-y-16">
                {/* Other Posts in Same Category */}
                <RelatedPostsSection
                    title={`Other posts in ${post.category}`}
                    posts={getRelatedPostsByCategory(post.id, post.category)}
                />

                {/* Other Posts by Same Author */}
                <RelatedPostsSection
                    title={`Other posts by ${post.author}`}
                    posts={getRelatedPostsByAuthor(post.id, post.author)}
                />
            </div>

            {/* Available Categories */}
            <div className="mt-16 pt-8 border-t border-gray-700">
                <h3 className="text-2xl font-bold mb-6 text-white">Explore Categories</h3>
                <div className="flex flex-wrap gap-3">
                    {getAllCategories().map(category => (
                        <Link
                            key={category}
                            to={`/augury?category=${encodeURIComponent(category)}`}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors border border-gray-600 hover:border-gray-500"
                        >
                            {category}
                        </Link>
                    ))}
                </div>
            </div>

            {/* Back Navigation Footer */}
            <div className="mt-16 pt-8 border-t border-gray-700">
                <Link
                    to="/augury"
                    className="text-accent-light hover:text-accent transition-colors flex items-center group"
                >
                    <span className="transform group-hover:-translate-x-1 transition-transform">←</span>
                    <span className="ml-2">Back to Augury</span>
                </Link>
            </div>

            {/* Sticky Email Subscription Banner - Mobile */}
            <div className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${isEmailBannerMinimized ? 'translate-y-full' : 'translate-y-0'}`}>
                <div className="bg-gray-900 bg-opacity-90 border-t border-gray-700/50 p-4 shadow-lg" style={{
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                }}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-white">Stay Updated</h3>
                        <button
                            onClick={() => setIsEmailBannerMinimized(true)}
                            className="text-gray-400 hover:text-white transition-colors"
                            aria-label="Minimize banner"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <a
                        href="https://pastasauce.notion.site/2262472aefc58012885deef5fbdc9c6c"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full bg-accent bg-opacity-90 text-white py-3 px-4 rounded-lg font-medium hover:bg-accent transition-colors"
                        style={{
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                        }}
                    >
                        <svg
                            className="w-4 h-4 mr-2"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                        Receive our Augury updates
                    </a>
                </div>
            </div>

            {/* Minimized Banner Toggle - Mobile */}
            {isEmailBannerMinimized && (
                <div className="fixed bottom-4 right-4 z-50 md:hidden">
                    <button
                        onClick={() => setIsEmailBannerMinimized(false)}
                        className="bg-accent text-white p-3 rounded-full shadow-lg hover:bg-opacity-90 transition-colors"
                        aria-label="Show email subscription"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default AuguryPostPage; 